import React, { useCallback, useState } from 'react'
import { Button } from 'reactstrap'
import { WhiteBox } from '@/components/Layout'
import { FileAttachments, defaultChangeHandler, operations } from '@/components/fileAttachments'
import LoadingIndicator from '@/components/LoadingIndicator'
import { DataLoader } from '@/components/dataLoader'
import Icon from '@/components/Icon'
import { ReportSubmissionGroupState } from '@/enums/ReportSubmissionGroupState'
import getComponentKey from '@/utils/getComponentKey'
import { useLocalization } from '@/hooks/localization'
import { useDispatch } from '@/hooks/dispatch'
import { actionCreators } from '@/store/ReportSubmissionGroup'
import { ReportGroupProcessState } from '@/enums/reportGroupProcessState'

const UploadReportsInternal = React.memo(({ parentId,
  disabled,
  reportSubmissionGroupState,
  removeReportUploads,
  addReportUploads,
  applyReportUpload,
  downloadAction,
  dataSource,
  loading,
  data,
  disableImport
}) => {
  const [attachments, setAttachments] = useState(data)
  const [setInstanceGroupState] = useDispatch([actionCreators.setInstanceGroupState])
  const { localize } = useLocalization()

  const onAttachmentChange = useCallback(e => {
    setAttachments(defaultChangeHandler(e, attachments))
    if (e.operation === operations.remove) removeReportUploads(e.file.id)
  }, [attachments, removeReportUploads])

  const addReportUploadsInternal = useCallback(() => {
    addReportUploads({
      entityId: parentId,
      fileIds: attachments.map(x => x.id)
    })
  }, [addReportUploads, attachments, parentId])

  const onApplyReportUpload = useCallback((id) => {
    setInstanceGroupState(ReportGroupProcessState.enum.DataImporting)
    applyReportUpload({ reportSubmissionGroupId: parentId, fileAttachmentId: id })
  }, [applyReportUpload, parentId, setInstanceGroupState])

  const hasCustomActions = !!(
    (reportSubmissionGroupState === false || reportSubmissionGroupState === null || reportSubmissionGroupState === undefined) ||
    (reportSubmissionGroupState === ReportSubmissionGroupState.enum.Unbegun ||
      reportSubmissionGroupState === ReportSubmissionGroupState.enum.Incomplete ||
      reportSubmissionGroupState === ReportSubmissionGroupState.enum.Rejected))

  const customActions = (hasCustomActions ? [
    {
      buttonContent: <Icon name='right-arrow' title={localize('Reporting.ApplyReportUpload')} />,
      onClick: onApplyReportUpload,
      disabled: disableImport
    }
  ] : null)

  return <FileAttachments
    readonly={disabled}
    attachments={attachments}
    downloadAction={downloadAction}
    onChange={onAttachmentChange}
    onFilesAdded={addReportUploadsInternal}
    customActions={customActions}
    isRemoveEnabled={hasCustomActions}
    accept='.xlsx,.xml'
  />
})

const UploadReports = React.memo(({
  adding,
  applying,
  dataSource,
  reportSubmissionGroupProcessState,
  ...props
}) => {
  const [uploadReportsKey, setUploadReportsKey] = useState(getComponentKey())
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const { localize } = useLocalization()

  const onRefresh = useCallback(() => setUploadReportsKey(getComponentKey()), [])
  const onStateUpdate = useCallback(({ data, loading }) => {
    setData(data)
    setLoading(loading)
  }, [setData, setLoading])

  return <WhiteBox
    header='Reporting.ReportUploads'
    buttons={<Button onClick={onRefresh}>
      {localize('Common.Refresh')}
    </Button>}>
    <DataLoader key={uploadReportsKey} dataSource={dataSource} onStateUpdate={onStateUpdate}>
      {loading && <LoadingIndicator />}
      {!loading &&
        <UploadReportsInternal {...props} data={data}
          disableImport={adding || applying || reportSubmissionGroupProcessState > 0} />}
    </DataLoader>
  </WhiteBox>
})

export { UploadReports }



// WEBPACK FOOTER //
// ./src/components/reporting/reportUpload.js